import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { Package, Mail, MessageSquare, Eye, Sparkles, Lock, Clock, Calendar, Save } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../lib/api";

const AdminDashboard = () => {
  const { authHeaders } = useOutletContext();
  const [recent, setRecent] = useState({ orders: [], contacts: [] });
  const [hidden, setHidden] = useState([]);
  const [releasingId, setReleasingId] = useState(null);

  const loadHidden = () => {
    axios
      .get(`${API}/admin/products`, { headers: authHeaders })
      .then((r) => setHidden((r.data || []).filter((p) => p.revealed === false)))
      .catch(() => {});
  };

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/orders`, { headers: authHeaders }),
      axios.get(`${API}/admin/contacts`, { headers: authHeaders }),
    ])
      .then(([o, c]) =>
        setRecent({ orders: o.data.slice(0, 5), contacts: c.data.slice(0, 5) })
      )
      .catch(() => {});
    loadHidden();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authHeaders]);

  const releaseProduct = async (product) => {
    if (
      !window.confirm(
        `Weet je zeker dat je "${product.name}" wilt onthullen? Hij wordt direct zichtbaar op de site en de teaser banner verdwijnt.`
      )
    )
      return;
    setReleasingId(product.id);
    try {
      await axios.post(
        `${API}/admin/products/${product.id}/reveal`,
        {},
        { headers: authHeaders }
      );
      toast.success(`${product.name} is nu live!`);
      loadHidden();
    } catch {
      toast.error("Release mislukt — probeer opnieuw");
    } finally {
      setReleasingId(null);
    }
  };

  const reHide = async (product) => {
    if (
      !window.confirm(
        `Wil je "${product.name}" weer verbergen? Hij verdwijnt van de site en de teaser banner verschijnt opnieuw.`
      )
    )
      return;
    setReleasingId(product.id);
    try {
      await axios.patch(
        `${API}/admin/products/${product.id}`,
        { revealed: false },
        { headers: authHeaders }
      );
      toast.success(`${product.name} weer verborgen`);
      loadHidden();
    } catch {
      toast.error("Verbergen mislukt");
    } finally {
      setReleasingId(null);
    }
  };

  return (
    <div data-testid="admin-dashboard" className="space-y-8">
      {/* Teaser & Reveal — only renders when there is something to release */}
      {hidden.length > 0 && (
        <ReleaseSection
          hidden={hidden}
          releasingId={releasingId}
          onRelease={releaseProduct}
          onScheduleSaved={loadHidden}
          authHeaders={authHeaders}
        />
      )}

      <Section
        icon={Package}
        title="LAATSTE BESTELLINGEN"
        empty="Nog geen bestellingen."
        items={recent.orders.map((o) => ({
          key: o.id,
          left: `#${o.id.slice(0, 8).toUpperCase()} — ${o.full_name}`,
          right: `€ ${o.total.toFixed(2)} · ${o.status}`,
          sub: new Date(o.created_at).toLocaleString("nl-NL"),
        }))}
      />
      <Section
        icon={MessageSquare}
        title="LAATSTE BERICHTEN"
        empty="Nog geen contactberichten."
        items={recent.contacts.map((c) => ({
          key: c.id,
          left: c.name,
          right: c.subject || "—",
          sub: c.email,
        }))}
      />

      {/* Quick re-hide tool (if any revealed product needs to be re-hidden) — handy escape hatch */}
      <HiddenManager onReHide={reHide} releasingId={releasingId} authHeaders={authHeaders} />

      <div className="border border-[var(--tha-border)] bg-[#0a0a0a] p-6 flex items-start gap-3">
        <Mail size={16} className="text-[var(--tha-red)] mt-1" />
        <div className="text-sm text-[var(--tha-muted)]">
          E-mails worden automatisch verstuurd via Gmail SMTP. Contactberichten gaan ook
          door naar <span className="text-white">tikhemaannl@gmail.com</span>.
        </div>
      </div>
    </div>
  );
};

const ReleaseSection = ({ hidden, releasingId, onRelease, onScheduleSaved, authHeaders }) => (
  <div
    data-testid="release-section"
    className="border bg-[#0a0a0a] relative overflow-hidden"
    style={{
      borderColor: "#d4a04a",
      background:
        "radial-gradient(ellipse at top right, #d4a04a22 0%, transparent 60%), #0a0a0a",
    }}
  >
    <div className="px-5 py-4 border-b border-[var(--tha-border)] flex items-center gap-2">
      <Sparkles size={14} style={{ color: "#d4a04a" }} />
      <span className="text-xs font-mono-x tracking-widest text-white">
        TEASER & RELEASE
      </span>
      <span className="ml-auto text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">
        {hidden.length} VERBORGEN
      </span>
    </div>

    <div className="p-6">
      <p className="text-sm text-[var(--tha-muted)] mb-5 max-w-2xl">
        Deze producten zijn nog niet zichtbaar op de site. Op de homepage zien
        bezoekers alleen een teaser-banner met countdown. Op de geplande releasedatum
        gaat hij automatisch live en krijgen alle nieuwsbriefleden direct een mail.
        Druk op <span className="text-white">RELEASE NU</span> om dit handmatig
        vooraf te doen.
      </p>

      <ul className="space-y-3">
        {hidden.map((p) => (
          <HiddenProductRow
            key={p.id}
            product={p}
            accent={p.accent_color || "#d4a04a"}
            releasingId={releasingId}
            onRelease={onRelease}
            onScheduleSaved={onScheduleSaved}
            authHeaders={authHeaders}
          />
        ))}
      </ul>
    </div>
  </div>
);

const HiddenProductRow = ({ product, accent, releasingId, onRelease, onScheduleSaved, authHeaders }) => {
  const [now, setNow] = useState(() => Date.now());
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(toDatetimeLocal(product.reveal_at));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!product.reveal_at) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [product.reveal_at]);

  const target = product.reveal_at ? new Date(product.reveal_at).getTime() : null;
  const diff = target ? Math.max(0, target - now) : null;

  const saveSchedule = async () => {
    setSaving(true);
    try {
      const isoToSend = draft ? new Date(draft).toISOString() : "";
      await axios.patch(
        `${API}/admin/products/${product.id}`,
        { reveal_at: isoToSend },
        { headers: authHeaders }
      );
      toast.success("Releasedatum opgeslagen");
      setEditing(false);
      onScheduleSaved && onScheduleSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };

  const clearSchedule = async () => {
    if (!window.confirm("Releasedatum verwijderen? Hij wordt dan niet meer automatisch onthuld.")) return;
    setSaving(true);
    try {
      await axios.patch(
        `${API}/admin/products/${product.id}`,
        { reveal_at: "" },
        { headers: authHeaders }
      );
      toast.success("Releasedatum verwijderd");
      setEditing(false);
      onScheduleSaved && onScheduleSaved();
    } catch {
      toast.error("Wissen mislukt");
    } finally {
      setSaving(false);
    }
  };

  return (
    <li
      data-testid={`hidden-product-${product.id}`}
      className="flex flex-col gap-4 p-4 border border-[var(--tha-border)] bg-[#070707]"
    >
      <div className="flex items-center gap-4">
        <div
          className="w-14 h-14 shrink-0 border flex items-center justify-center overflow-hidden"
          style={{ borderColor: accent }}
        >
          {product.image ? (
            <img
              src={product.image}
              alt={product.name}
              className="w-full h-full object-cover"
              style={{ filter: "brightness(0.7)" }}
            />
          ) : (
            <Lock size={16} style={{ color: accent }} />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-2xl text-white leading-none">
            {product.name.toUpperCase()}
          </div>
          <div className="text-xs text-[var(--tha-muted)] mt-1 truncate">
            {product.tagline || "—"}
          </div>
          <div
            className="mt-1 text-[10px] font-mono-x tracking-widest"
            style={{ color: accent }}
          >
            STATUS: VERBORGEN — TEASER ACTIEF
          </div>
        </div>
        <button
          data-testid={`release-button-${product.id}`}
          onClick={() => onRelease(product)}
          disabled={releasingId === product.id}
          className="shrink-0 px-5 py-3 font-display tracking-wider text-sm inline-flex items-center gap-2 transition-transform hover:scale-[1.02] disabled:opacity-60"
          style={{ background: accent, color: "#050505" }}
        >
          <Eye size={14} />
          {releasingId === product.id ? "..." : "RELEASE NU"}
        </button>
      </div>

      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 pt-3 border-t border-[var(--tha-border)]">
        <div className="flex items-center gap-2 text-xs text-[var(--tha-muted)]">
          <Calendar size={12} style={{ color: accent }} />
          <span className="font-mono-x tracking-widest">GEPLAND:</span>
          <span className="text-white">
            {product.reveal_at
              ? new Date(product.reveal_at).toLocaleString("nl-NL", {
                  dateStyle: "long",
                  timeStyle: "short",
                  timeZone: "Europe/Amsterdam",
                }) + " (NL-tijd)"
              : "GEEN PLANNING"}
          </span>
        </div>

        {target && !editing && (
          <div
            data-testid={`countdown-${product.id}`}
            className="sm:ml-auto inline-flex items-center gap-2 text-xs font-mono-x tracking-widest"
            style={{ color: accent }}
          >
            <Clock size={12} />
            {diff === 0 ? "DUE — auto-reveal binnen 60s" : formatDiff(diff)}
          </div>
        )}

        {!editing ? (
          <button
            data-testid={`edit-schedule-${product.id}`}
            onClick={() => {
              setDraft(toDatetimeLocal(product.reveal_at));
              setEditing(true);
            }}
            className="btn-ghost-red text-xs px-3 py-2 inline-flex items-center gap-1"
          >
            <Calendar size={12} /> WIJZIG DATUM
          </button>
        ) : (
          <div className="sm:ml-auto flex flex-col sm:flex-row gap-2 items-start sm:items-center">
            <input
              data-testid={`schedule-input-${product.id}`}
              type="datetime-local"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="px-3 py-2 text-xs bg-[#0a0a0a] border border-[var(--tha-border)] text-white focus:outline-none"
              style={{ colorScheme: "dark" }}
            />
            <button
              data-testid={`schedule-save-${product.id}`}
              onClick={saveSchedule}
              disabled={saving}
              className="text-xs px-3 py-2 inline-flex items-center gap-1 font-display tracking-wider disabled:opacity-60"
              style={{ background: accent, color: "#050505" }}
            >
              <Save size={12} /> OPSLAAN
            </button>
            <button
              onClick={() => setEditing(false)}
              className="text-xs px-3 py-2 text-[var(--tha-muted)] hover:text-white"
            >
              ANNULEREN
            </button>
            {product.reveal_at && (
              <button
                onClick={clearSchedule}
                disabled={saving}
                className="text-xs px-3 py-2 text-[var(--tha-muted)] hover:text-[var(--tha-red)] disabled:opacity-60"
              >
                WISSEN
              </button>
            )}
          </div>
        )}
      </div>
    </li>
  );
};

const formatDiff = (ms) => {
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  if (d > 0) return `OVER ${d}D ${h}U ${m}M`;
  return `OVER ${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const toDatetimeLocal = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return "";
  }
};

// Optional re-hide control: only visible if you want to put a recently revealed
// product back into teaser-mode (e.g. before deploy). Keeps the dashboard simple
// when there's nothing to re-hide.
const HiddenManager = ({ onReHide, releasingId, authHeaders }) => {
  const [revealedExtras, setRevealedExtras] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/admin/products`, { headers: authHeaders })
      .then((r) => {
        // Only suggest re-hide for products that are still "coming_soon" — production
        // shots that already sell shouldn't be hideable from this casual button.
        const candidates = (r.data || []).filter(
          (p) => p.revealed === true && p.coming_soon === true && p.id === "dark-drop"
        );
        setRevealedExtras(candidates);
      })
      .catch(() => {});
  }, [authHeaders, releasingId]);

  if (revealedExtras.length === 0) return null;
  return (
    <div className="border border-[var(--tha-border)] bg-[#0a0a0a]">
      <div className="px-5 py-4 border-b border-[var(--tha-border)] flex items-center gap-2">
        <Lock size={14} className="text-[var(--tha-muted)]" />
        <span className="text-xs font-mono-x tracking-widest text-white">
          RE-HIDE TEASER
        </span>
      </div>
      <ul>
        {revealedExtras.map((p) => (
          <li
            key={p.id}
            className="px-5 py-3 flex items-center justify-between gap-3"
          >
            <div className="text-sm text-white">{p.name}</div>
            <button
              data-testid={`rehide-button-${p.id}`}
              onClick={() => onReHide(p)}
              disabled={releasingId === p.id}
              className="btn-ghost-red px-3 py-2 text-xs inline-flex items-center gap-2 disabled:opacity-60"
            >
              <Lock size={12} /> VERBERG WEER
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

const Section = ({ icon: Icon, title, items, empty }) => (
  <div className="border border-[var(--tha-border)] bg-[#0a0a0a]">
    <div className="px-5 py-4 border-b border-[var(--tha-border)] flex items-center gap-2">
      <Icon size={14} className="text-[var(--tha-red)]" />
      <span className="text-xs font-mono-x tracking-widest text-white">{title}</span>
    </div>
    {items.length === 0 ? (
      <div className="p-8 text-center text-sm text-[var(--tha-muted)]">{empty}</div>
    ) : (
      <ul>
        {items.map((i) => (
          <li
            key={i.key}
            className="px-5 py-4 border-b border-[var(--tha-border)] last:border-0 flex items-center justify-between gap-4"
          >
            <div className="min-w-0">
              <div className="text-sm text-white truncate">{i.left}</div>
              <div className="text-xs text-[var(--tha-muted)] mt-0.5 truncate">{i.sub}</div>
            </div>
            <div className="text-xs font-mono-x tracking-widest text-[var(--tha-muted)] shrink-0">
              {i.right}
            </div>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export default AdminDashboard;
